import { CardProps } from '@modules/configuration/Rules/components/display/Card';
import { DropdownActions } from '@modules/configuration/Rules/components/interaction/DropdownActions';
import { faChevronDown, faGripVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { cn } from '@shared/utils/utils';
import { isMobile } from 'react-device-detect';
import { memo, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientRuleInfoDisplay } from './ClientRuleInfoDisplay';

function areClientRuleBoardCardPropsEqual(prev: CardProps, next: CardProps) {
  return (
    prev.item === next.item &&
    prev.index === next.index &&
    prev.className === next.className &&
    prev.isReadOnly === next.isReadOnly &&
    prev.canEdit === next.canEdit &&
    prev.canDelete === next.canDelete &&
    prev.isListReordering === next.isListReordering &&
    prev.isDragPreview === next.isDragPreview &&
    prev.isActive === next.isActive &&
    prev.isLoading === next.isLoading &&
    prev.dragHandleProps === next.dragHandleProps
  );
}

/**
 * Cabecera estilo Zonas de cobertura. En modo reordenación (o vista previa de arrastre)
 * solo se muestra la cabecera para que el reorden con DnD sea manejable.
 */
const ClientRuleBoardCardInner = ({
  item: rule,
  index,
  className = '',
  onEdit,
  onDelete,
  dragHandleProps,
  isActive,
  onToggleActive,
  isReadOnly = false,
  canEdit = true,
  canDelete = true,
  isListReordering = false,
  isDragPreview = false,
}: CardProps) => {
  const { t } = useTranslation('rulesModule');
  const order = index + 1;
  const compact = isListReordering || isDragPreview;
  const [groupOpen, setGroupOpen] = useState(true);
  const bodyId = useId();
  const wasCompactRef = useRef(compact);

  // Tras reorden, las cards vuelven de modo compacta (sin cuerpo/chevron) a normal;
  // reseteamos a abierto y evitamos desincronización que dejaba el detalle plegado aunque !compact.
  useLayoutEffect(() => {
    if (wasCompactRef.current && !compact) {
      setGroupOpen(true);
    }
    wasCompactRef.current = compact;
  }, [compact]);

  const openEdit = () => {
    if (canEdit && onEdit) onEdit();
  };

  return (
    <div
      className={cn(
        'mb-4 overflow-hidden rounded-lg border border-border bg-card text-card-foreground',
        compact && 'shadow-sm',
        isDragPreview && 'ring-2 ring-border shadow-xl',
        className,
      )}
      data-testid={`client-rule-card-${rule.id}`}
    >
      <div
        className={cn(
          'bg-muted/30 px-4',
          compact ? 'border-b border-border py-2' : 'py-3.5',
          !compact && (groupOpen ? 'border-b border-border' : 'rounded-b-lg'),
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:gap-3">
            {!isReadOnly && canEdit && (isDragPreview || dragHandleProps) && (
              <div
                className="shrink-0 text-muted-foreground"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {isDragPreview ? (
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-md text-muted-foreground/80"
                    aria-hidden
                  >
                    <FontAwesomeIcon icon={faGripVertical} className="h-5 w-5" />
                  </span>
                ) : (
                  <button
                    type="button"
                    className="flex h-12 w-12 min-h-[48px] min-w-[48px] items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground active:bg-muted"
                    aria-label={t('clientList.board.drag_label')}
                    style={
                      isMobile
                        ? {
                            touchAction: 'none',
                            WebkitTouchCallout: 'none',
                            WebkitUserSelect: 'none',
                            userSelect: 'none',
                          }
                        : { touchAction: 'none' }
                    }
                    {...dragHandleProps}
                  >
                    <FontAwesomeIcon icon={faGripVertical} className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
            {!compact && (
              <button
                type="button"
                className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
                onClick={() => setGroupOpen((o) => !o)}
                aria-expanded={groupOpen}
                aria-controls={bodyId}
                title={groupOpen ? t('clientList.display.collapse_section') : t('clientList.display.expand_section')}
              >
                <FontAwesomeIcon icon={faChevronDown} className={cn('h-4 w-4', groupOpen && 'rotate-180')} />
              </button>
            )}
            <div
              className={cn('min-w-0 flex-1', canEdit && onEdit && !compact && 'cursor-pointer')}
              onClick={canEdit && onEdit && !compact ? openEdit : undefined}
            >
              <div className="flex min-w-0 items-baseline gap-2.5">
                <span className="shrink-0 text-sm font-medium tabular-nums text-muted-foreground">#{order}</span>
                <h3 className="min-w-0 truncate text-base font-semibold text-title sm:text-lg">{rule.name}</h3>
              </div>
            </div>
          </div>
          <div
            className="flex shrink-0 items-center gap-2 sm:gap-3"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {!isDragPreview && (
              <Switch
                checked={rule.is_active}
                onCheckedChange={isReadOnly || !canEdit ? undefined : onToggleActive}
                disabled={isReadOnly || !canEdit}
                className="data-[state=checked]:bg-primary"
                aria-label={t('table.status')}
              />
            )}
            {!isReadOnly && (canEdit || canDelete) && !isDragPreview && (
              <DropdownActions
                onEdit={onEdit}
                onDelete={onDelete}
                isActive={isActive}
                onToggleActive={onToggleActive}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            )}
          </div>
        </div>
      </div>

      {!compact && (
        <div
          id={bodyId}
          className={cn('px-4 py-3 sm:px-5 sm:py-4', !groupOpen && 'hidden', canEdit && onEdit && 'cursor-pointer')}
          onClick={canEdit && onEdit ? openEdit : undefined}
        >
          <ClientRuleInfoDisplay rule={rule} />
        </div>
      )}
    </div>
  );
};

export const ClientRuleBoardCard = memo(ClientRuleBoardCardInner, areClientRuleBoardCardPropsEqual);
ClientRuleBoardCardInner.displayName = 'ClientRuleBoardCard';
